import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Plus, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  draft: 'muted', scheduled: 'warn', active: 'ok', paused: 'warn', completed: 'muted',
};

const CampaignList = ({ lob, channelFilter, title, testKey }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [campaigns, setCampaigns] = useState([]);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    if (!lob) return;
    const url = `/campaigns?lob_id=${lob.id}${channelFilter ? `&channel=${channelFilter}` : ''}`;
    api.get(url).then((r) => setCampaigns(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [lob?.id, channelFilter]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await api.delete(`/campaigns/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-toolbar" data-testid={`${testKey}-toolbar`}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">{title}</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid={`${testKey}-add-btn`}>
          <Plus size={11} style={{ marginRight: 4 }} />New campaign
        </button>
      </div>

      {err && <div className="module-banner err">{err}</div>}

      <div className="content-area">
        {campaigns.length === 0 ? (
          <div className="empty-state" data-testid={`${testKey}-empty`}>
            <div className="ttl">No campaigns yet</div>
            <div className="sub">Create your first {channelFilter || 'multi-channel'} campaign.</div>
          </div>
        ) : (
          <div className="card">
            <table className="tbl">
              <thead><tr><th>Name</th><th>Channel</th><th>Status</th><th>Sent</th><th>Open %</th><th>Click %</th><th>Conv %</th><th></th></tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} data-testid={`${testKey}-row-${c.id}`}>
                    <td><button className="btn-link" onClick={() => setEditing(c)} data-testid={`edit-camp-${c.id}`}>{c.name}</button></td>
                    <td>{c.channel}</td>
                    <td><span className={`tag-pill ${STATUS_COLORS[c.status] || 'muted'}`}>{c.status}</span></td>
                    <td>{c.sent || 0}</td>
                    <td>{c.open_rate || 0}%</td>
                    <td>{c.click_rate || 0}%</td>
                    <td>{c.conv_rate || 0}%</td>
                    <td><button className="btn-link" onClick={() => del(c.id)} data-testid={`del-camp-${c.id}`} style={{ color: 'var(--error)' }}><Trash2 size={11} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showCreate || editing) && (
        <CampaignModal lob={lob} channel={channelFilter} campaign={editing} onClose={(saved) => { setShowCreate(false); setEditing(null); if (saved) load(); }} />
      )}
    </>
  );
};

const CampaignModal = ({ lob, channel, campaign, onClose }) => {
  const [form, setForm] = useState(campaign || {
    lob_id: lob.id, name: '', channel: channel || 'email', status: 'draft',
    sent: 0, open_rate: 0, click_rate: 0, conv_rate: 0, scheduled_for: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    const num = (n) => Number(n) || 0;
    const payload = { ...form, sent: num(form.sent), open_rate: num(form.open_rate), click_rate: num(form.click_rate), conv_rate: num(form.conv_rate) };
    try {
      if (campaign) await api.patch(`/campaigns/${campaign.id}`, payload);
      else await api.post('/campaigns', payload);
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="campaign-modal">
        <div className="modal-title">{campaign ? 'Edit campaign' : 'New campaign'}</div>
        <label className="f-label">Name</label>
        <input className="f-input" value={form.name} onChange={(e) => set('name', e.target.value)} required data-testid="cmp-name" />
        <div className="modal-row">
          <div><label className="f-label">Channel</label>
            <select className="f-select" value={form.channel} onChange={(e) => set('channel', e.target.value)} data-testid="cmp-channel">
              <option value="email">Email</option><option value="sms">SMS</option><option value="social">Social</option>
            </select>
          </div>
          <div><label className="f-label">Status</label>
            <select className="f-select" value={form.status} onChange={(e) => set('status', e.target.value)} data-testid="cmp-status">
              <option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="active">Active</option><option value="paused">Paused</option><option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Sent</label><input className="f-input" type="number" value={form.sent} onChange={(e) => set('sent', e.target.value)} /></div>
          <div><label className="f-label">Open %</label><input className="f-input" type="number" step="0.1" value={form.open_rate} onChange={(e) => set('open_rate', e.target.value)} /></div>
          <div><label className="f-label">Click %</label><input className="f-input" type="number" step="0.1" value={form.click_rate} onChange={(e) => set('click_rate', e.target.value)} /></div>
          <div><label className="f-label">Conv %</label><input className="f-input" type="number" step="0.1" value={form.conv_rate} onChange={(e) => set('conv_rate', e.target.value)} /></div>
        </div>
        <label className="f-label">Notes</label>
        <textarea className="f-textarea" rows={3} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
        {err && <div className="f-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="cmp-submit">{busy ? 'Saving…' : (campaign ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

// Email module: campaigns filtered to channel=email
const Email = ({ lob }) => <CampaignList lob={lob} channelFilter="email" title="Email" testKey="email" />;
export default Email;
export { CampaignList };
